import { useEffect, useState } from "react";
import type { ChainUnplaced } from "@dentacore/shared-types";

import { proposeChain } from "../api/plans";
import { atLocation } from "../api/client";
import styles from "./calendar.module.css";
import { instantOfWallClock, wallClockOfInstant } from "./zoned-time";

/**
 * The rest of a treatment plan, booked from the same form — STORY-131b (CH2).
 *
 * Choosing a plan — or a multi-visit procedure (PV3) — in the booking form lists every remaining
 * visit under the one being booked, each **its gap after the one before, at the
 * same time**, in the same chair and for the same length (CH2). A proposed time
 * that clashes has already been moved to the nearest free one by the server
 * (CH3), and says so. **Every date and time is an ordinary box** the person can
 * change before pressing Book; clearing one leaves that visit for later, where
 * it waits to be booked (CH9).
 */

/** One remaining visit, as the form shows it. */
export interface ChainVisit {
  /**
   * The plan's visit, or null for a procedure whose course is only created
   * when Book is pressed (PV3) — its visits then map by `visitNumber`.
   */
  partId: string | null;
  /** 1-based, as the plan numbers it. */
  visitNumber: number;
  title: string | null;
  gapDays: number | null;
  /** This visit's own length, when a procedure's visits differ (PV1). */
  durationMinutes?: number;
}

export interface ChainRow extends ChainVisit {
  /** `YYYY-MM-DD` in the branch's zone; empty means "not now". */
  day: string;
  /** `HH:MM`; empty means "not now". */
  clock: string;
  /** The server moved it off its gap's target because of a clash (CH3). */
  moved: boolean;
  unplaced: ChainUnplaced | null;
}

/**
 * Ask the server where the visits would go, whenever what they count from
 * changes — chair, day, time or length — and keep the person's edits otherwise.
 *
 * **Debounced**, because typing a time fires a change per keystroke and each is
 * a request; a quarter of a second is below what reads as lag.
 */
export function useChainRows(input: {
  /** **Memoised by the caller** — a new array is a new request. */
  visits: readonly ChainVisit[];
  chairId: string;
  /** The patient being booked, so a proposal skips their other appointments. */
  patientId: string | null;
  day: string;
  clock: string;
  durationMinutes: number;
  timezone: string;
  enabled: boolean;
  /** The location being booked into on the combined calendar (CF-D15); null for the active one. */
  branchId?: string | null;
}): {
  rows: ChainRow[];
  loading: boolean;
  edit: (index: number, change: Partial<ChainRow>) => void;
} {
  const { visits, chairId, patientId, day, clock, durationMinutes, timezone, enabled } = input;
  const branchId = input.branchId ?? null;
  const [rows, setRows] = useState<ChainRow[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const usable = enabled && visits.length > 0 && chairId !== "" && durationMinutes > 0;
    if (!usable) {
      setRows([]);
      return;
    }

    const controller = new AbortController();
    setLoading(true);

    const timer = setTimeout(() => {
      atLocation(branchId, () =>
        proposeChain(
          {
            chairId,
            ...(patientId === null ? {} : { patientId }),
            startsAt: instantOfWallClock(day, clock, timezone).toISOString(),
            durationMinutes,
            gaps: visits.map((visit) => visit.gapDays),
            // Each visit's own length when the procedure gives one (PV1), so a
            // 60-minute prep and a 30-minute try-in are each checked at theirs.
            ...(visits.some((visit) => visit.durationMinutes !== undefined)
              ? { durations: visits.map((visit) => visit.durationMinutes ?? durationMinutes) }
              : {}),
          },
          controller.signal,
        ),
      )
        .then(({ proposals }) => {
          setRows(
            visits.map((visit, index) => {
              const proposal = proposals[index];
              const placed =
                proposal?.startsAt === null || proposal?.startsAt === undefined
                  ? null
                  : wallClockOfInstant(new Date(proposal.startsAt), timezone);
              return {
                ...visit,
                day: placed?.day ?? "",
                clock: placed?.clock ?? "",
                moved: proposal?.moved ?? false,
                unplaced: proposal?.unplaced ?? null,
              };
            }),
          );
        })
        .catch(() => {
          // No proposal means empty boxes, not a broken form: every visit can
          // still be dated by hand, or left for later.
          if (controller.signal.aborted) return;
          setRows(
            visits.map((visit) => ({ ...visit, day: "", clock: "", moved: false, unplaced: null })),
          );
        })
        .finally(() => {
          if (!controller.signal.aborted) setLoading(false);
        });
    }, 250);

    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [visits, chairId, patientId, day, clock, durationMinutes, timezone, enabled, branchId]);

  const edit = (index: number, change: Partial<ChainRow>): void => {
    setRows((current) =>
      current.map((row, at) =>
        at === index ? { ...row, ...change, moved: false, unplaced: null } : row,
      ),
    );
  };

  return { rows, loading, edit };
}

/** Why a visit has no proposed time, said as what to do about it. */
function noteFor(row: ChainRow): string | null {
  if (row.moved) return "Moved to the nearest free time — the gap's own time was taken.";
  switch (row.unplaced) {
    case "NO_GAP":
      return "No gap set for this visit. Pick a date, or leave it for later.";
    case "AFTER_UNPLACED":
      return "Counts from the visit before, which has no date. Pick one, or leave it for later.";
    case "NO_FREE_SLOT":
      return "Nothing free in this chair for two months. Pick a date, or leave it for later.";
    default:
      return null;
  }
}

export function ChainVisits({
  rows,
  loading,
  include,
  onInclude,
  onEdit,
}: {
  rows: readonly ChainRow[];
  loading: boolean;
  include: boolean;
  onInclude: (next: boolean) => void;
  onEdit: (index: number, change: Partial<ChainRow>) => void;
}): React.JSX.Element | null {
  if (rows.length === 0 && !loading) return null;

  const dated = rows.filter((row) => row.day !== "" && row.clock !== "").length;

  return (
    <div className={styles.chain} data-testid="chain-visits">
      <label className={styles.checkField}>
        <input
          type="checkbox"
          checked={include}
          onChange={(event) => onInclude(event.target.checked)}
          data-testid="chain-include"
        />
        {/* While the first proposal is on its way there are no rows to count. */}
        {rows.length === 0
          ? "Also book the later visits"
          : rows.length === 1
            ? "Also book the next visit"
            : `Also book the next ${String(rows.length)} visits`}
      </label>

      {include &&
        rows.map((row, index) => {
          const note = noteFor(row);
          return (
            <div key={row.visitNumber} className={styles.chainRow} data-testid="chain-visit">
              <span className={styles.chainTitle}>
                Visit {row.visitNumber}
                {/*
                  The visit's own name, unless it only repeats the number —
                  "Visit 2 · Visit 2" was what a visit named "Visit 2" drew.
                */}
                {row.title !== null &&
                  row.title.trim() !== "" &&
                  row.title.trim().toLowerCase() !== `visit ${String(row.visitNumber)}` && (
                    <> · {row.title}</>
                  )}
                {row.gapDays !== null && (
                  <span className={styles.chainGap}>
                    {" "}
                    · book in {row.gapDays} {row.gapDays === 1 ? "day" : "days"}
                  </span>
                )}
              </span>
              <div className={styles.fieldPair}>
                <input
                  type="date"
                  className={styles.input}
                  value={row.day}
                  aria-label={`Date of visit ${String(row.visitNumber)}`}
                  onChange={(event) => onEdit(index, { day: event.target.value })}
                  data-testid="chain-day"
                />
                <input
                  type="time"
                  className={styles.input}
                  value={row.clock}
                  aria-label={`Time of visit ${String(row.visitNumber)}`}
                  onChange={(event) => onEdit(index, { clock: event.target.value })}
                  data-testid="chain-time"
                />
              </div>
              {note !== null && (
                <span
                  className={row.moved ? styles.hint : styles.providerWarning}
                  data-testid="chain-note"
                >
                  {note}
                </span>
              )}
            </div>
          );
        })}

      {include && !loading && dated < rows.length && (
        <span className={styles.hint}>
          A visit with no date is left for later, and appears in waiting to be booked.
        </span>
      )}
    </div>
  );
}
